import { join, call, cancel, fork, takeEvery, take, race, delay, put } from 'redux-saga/effects';

export function takeLeadingPerKey(patternOrChannel, worker, keySelector, ...args) {
  return fork(function* takeLeadingPerKeyFork() {
    const tasks = {};

    yield takeEvery(patternOrChannel, function* takeLeadingPerKeyForkTakeEvery(action) {
      const key = yield call(keySelector, action);

      if (!(tasks[key] && tasks[key].isRunning())) {
        tasks[key] = yield fork(worker, ...args, action);

        yield join(tasks[key]);

        if (tasks[key] && !tasks[key].isRunning()) {
          delete tasks[key];
        }
      }
    });
  });
}

export function debouncePerKey(ms, patternOrChannel, worker, keySelector, ...args) {
  return fork(function* debouncePerKeyFork() {
    const tasks = {};

    yield takeEvery(
      patternOrChannel,
      function* debouncePerKeyTakeEvery(action) {
        const key = yield call(keySelector, action);

        // Cancel existing debounce task for this key, if any
        if (tasks[key]) {
          yield cancel(tasks[key]);
        }

        const task = yield fork(function* debouncePerKeyWorker() {
          // Wait for the debounce delay
          yield delay(ms);

          // After delay with no new actions for this key → run worker
          yield call(worker, ...args, action);

          // Cleanup: only delete if we're still the latest task for this key
          if (tasks[key] === task) {
            delete tasks[key];
          }
        });

        tasks[key] = task;
      },
    );
  });
}

export function debouncePerType(ms, type, worker, ...args) {
  return debouncePerKey(ms, type, worker, () => type, ...args);
}

export function* takeOnce(pattern, saga, ...args) {
  const task = yield fork(function* takeOnceFork() {
    const action = yield take(pattern);
    yield call(saga, ...args.concat(action));
  });
  return task;
}

export function takeOncePerKey(patternOrChannel, worker, keySelector, ...args) {
  return fork(function* takeOncePerKeyFork() {
    const seen = new Set();

    while (true) {
      const action = yield take(patternOrChannel);
      const key = yield call(keySelector, action);

      if (key !== null && key !== undefined && !seen.has(key)) {
        seen.add(key);
        yield fork(worker, ...args, action);
      }
    }
  });
}

export function takeLatestPerKey(patternOrChannel, worker, keySelector, ...args) {
  return fork(function* takeLatestPerKeyFork() {
    const tasks = {};

    yield takeEvery(patternOrChannel, function* takeLatestPerKeyForkTakeEvery(action) {
      const key = yield call(keySelector, action);

      if (tasks[key]) {
        yield cancel(tasks[key]);
      }

      tasks[key] = yield fork(worker, ...args, action);

      yield join(tasks[key]);

      if (tasks[key] && !tasks[key].isRunning()) {
        delete tasks[key];
      }
    });
  });
}

export function takeEveryAndAggregate(pattern, actionToPayloadAggregator, callAction, debounce = 300) {
  return fork(function* takeEveryAndAggregateFork() {
    while (true) {
      const initAction = yield take(pattern);

      let actions = [initAction];

      while (true) {
        const { debounced, nextAction } = yield race({
          debounced: delay(debounce),
          nextAction: take(pattern),
        });

        if (nextAction) {
          actions = [...actions, nextAction];
        }

        if (debounced) {
          yield put(callAction(actionToPayloadAggregator(actions)));
          break;
        }
      }
    }
  });

}

// Runs the worker unless the pattern is dispatched first, which drops it
export const cancelledBy = (pattern, worker) => function* cancelledByWorker(...args) {
  yield race({ done: call(worker, ...args), cancelled: take(pattern) });
};
